/**
 * P-Ticket Retry Transport Wrapper
 *
 * Wraps any Transport implementation and automatically handles p-ticket retries.
 * Works with both HttpTransport and WebSocketTransport.
 *
 * Architecture:
 * - Inner transport: Pure protocol implementation (HTTP or WebSocket)
 * - Wrapper: Intercepts responses, detects p-ticket, retries automatically
 *
 * @example
 * ```typescript
 * // Wrap HTTP transport
 * const http = new HttpTransport(baseUrl);
 * const httpWithRetry = new PTicketRetryTransport(http);
 * httpWithRetry.post('/order/placeorder', {...});  // Auto-retries on p-ticket
 *
 * // Wrap WebSocket transport
 * const ws = new WebSocketTransport(wsUrl);
 * const wsWithRetry = new PTicketRetryTransport(ws);
 * await wsWithRetry.request('md/subscribeQuote', {...});  // Auto-retries on p-ticket
 * ```
 */

import type { Transport } from './types';
import { PTicketHandler } from './PTicketHandler';

/**
 * Extended transport interface for WebSocket
 */
interface ExtendedTransport extends Transport {
  request?(endpoint: string, body?: any): Promise<any>;
}

/**
 * P-Ticket Retry Transport
 *
 * Decorator pattern: Wraps any transport and adds automatic p-ticket retry.
 */
export class PTicketRetryTransport implements Transport {
  constructor(private innerTransport: ExtendedTransport) {}


  /**
   * GET request with p-ticket retry
   */
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    try {
      const result = await this.innerTransport.get(path, params);

      // Check for p-ticket and retry if present
      const retryResult = await PTicketHandler.handlePTicketRetry(
        result,
        params,
        (retryParams) => this.innerTransport.get(path, retryParams)
      );

      return retryResult !== null ? retryResult : result;

    } catch (error) {
      // Check if error response contains p-ticket
      if (error && typeof error === 'object' && 'data' in error) {
        const retryResult = await PTicketHandler.handlePTicketRetry(
          error.data,
          params,
          (retryParams) => this.innerTransport.get(path, retryParams)
        );

        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  }

  /**
   * POST request with p-ticket retry
   */
  async post<T = any>(path: string, body?: any): Promise<T> {
    try {
      const result = await this.innerTransport.post(path, body);

      // Check for p-ticket and retry if present
      const retryResult = await PTicketHandler.handlePTicketRetry(
        result,
        body,
        (retryBody) => this.innerTransport.post(path, retryBody)
      );

      return retryResult !== null ? retryResult : result;

    } catch (error) {
      // Check if error response contains p-ticket
      if (error && typeof error === 'object' && 'data' in error) {
        const retryResult = await PTicketHandler.handlePTicketRetry(
          error.data,
          body,
          (retryBody) => this.innerTransport.post(path, retryBody)
        );

        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  }

  /**
   * PUT request with p-ticket retry
   */
  async put<T = any>(path: string, body?: any): Promise<T> {
    try {
      const result = await this.innerTransport.put(path, body);

      const retryResult = await PTicketHandler.handlePTicketRetry(
        result,
        body,
        (retryBody) => this.innerTransport.put(path, retryBody)
      );

      return retryResult !== null ? retryResult : result;

    } catch (error) {
      if (error && typeof error === 'object' && 'data' in error) {
        const retryResult = await PTicketHandler.handlePTicketRetry(
          error.data,
          body,
          (retryBody) => this.innerTransport.put(path, retryBody)
        );

        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  }

  /**
   * DELETE request with p-ticket retry
   */
  async delete<T = any>(path: string): Promise<T> {
    try {
      const result = await this.innerTransport.delete(path);

      const retryResult = await PTicketHandler.handlePTicketRetry(
        result,
        undefined,
        () => this.innerTransport.delete(path)
      );

      return retryResult !== null ? retryResult : result;

    } catch (error) {
      if (error && typeof error === 'object' && 'data' in error) {
        const retryResult = await PTicketHandler.handlePTicketRetry(
          error.data,
          undefined,
          () => this.innerTransport.delete(path)
        );

        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  }

  /**
   * WebSocket request with p-ticket retry (if inner transport supports it)
   *
   * @param endpoint - WebSocket endpoint
   * @param body - Request body
   */
  async request(endpoint: string, body?: any): Promise<any> {
    if (!this.innerTransport.request) {
      throw new Error('Inner transport does not support request() method');
    }

    try {
      const result = await this.innerTransport.request(endpoint, body);

      // Check for p-ticket and retry if present
      const retryResult = await PTicketHandler.handlePTicketRetry(
        result,
        body,
        (retryBody) => this.innerTransport.request!(endpoint, retryBody)
      );

      return retryResult !== null ? retryResult : result;

    } catch (error) {
      // Check if error response contains p-ticket
      if (error && typeof error === 'object' && 'data' in error) {
        const retryResult = await PTicketHandler.handlePTicketRetry(
          error.data,
          body,
          (retryBody) => this.innerTransport.request!(endpoint, retryBody)
        );

        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  }

  /**
   * Get underlying transport (for accessing transport-specific methods)
   */
  unwrap(): ExtendedTransport {
    return this.innerTransport;
  }
}
